import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Table, Badge } from 'reactstrap'
import { FaDownload, FaArrowLeft, FaChevronLeft, FaChevronRight, FaUser, FaTag, FaGift, FaPercentage, FaLayerGroup } from 'react-icons/fa'
import dummyImg from '../../../../../../src/assets/images/product/1.png'
import { useParams, useNavigate, useLocation } from 'react-router'
import axios from 'axios'
import { baseURL, imageURL, orderURL } from '../../../../../Services/api/baseURL'
import moment from 'moment';

function ViewOrder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [navigationData, setNavigationData] = useState({
        currentIndex: -1,
        totalOrders: 0,
        ordersList: [],
        filters: {},
        isCustomerContext: false,
        customerName: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [newAddress, setNewAddress] = useState(null);

    const authData = useMemo(() => {
        try {
            return {
                token: JSON.parse(localStorage.getItem("token")),
                userRole: JSON.parse(localStorage.getItem("role_name")),
                userData: JSON.parse(localStorage.getItem("UserData"))
            };
        } catch (error) {
            console.error('Error parsing auth data:', error);
            return { token: null, userRole: null, userData: null };
        }
    }, []);

    useEffect(() => {
        if (location.state) {
            setNavigationData({
                currentIndex: location.state.currentIndex !== undefined ? location.state.currentIndex : -1,
                totalOrders: location.state.totalOrders || 0,
                ordersList: location.state.ordersList || [],
                filters: location.state.filters || {},
                isCustomerContext: location.state.isCustomerContext || false,
                customerName: location.state.customerName || ''
            });
        }
    }, [location.state]);

    const getOrderDetails = useCallback(async () => {
        if (!authData.token || !id) return;
        setLoading(true);
        try {
            const response = await axios.get(`${orderURL}/get-order/${id}`, {
                headers: { Authorization: `${authData.token}` }
            });
            if (response?.status === 200) {
                setOrderDetails(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching order details:', err);
        } finally {
            setLoading(false);
        }
    }, [id, authData.token]);

    const downloadInvoice = useCallback(async (orderId) => {
        if (!authData.token) return;
        try {
            const response = await axios.get(`${orderURL}/invoice-pdf/${orderId}`, {
                responseType: 'blob',
                headers: { Authorization: `${authData.token}` }
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `invoice-${orderId}.pdf`;
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Error downloading the invoice:', error);
        }
    }, [authData.token]);

    const handleBackToOrders = useCallback(() => {
        if (navigationData.isCustomerContext && navigationData.filters?.customerId) {
            navigate(`/customers/customer-detail/${navigationData.filters.customerId}`);
        } else {
            navigate('/orders');
        }
    }, [navigate, navigationData]);

    const handlePreviousOrder = useCallback(() => {
        const currentIndex = navigationData.currentIndex;
        if (currentIndex <= 0 || !navigationData.ordersList.length) return;
        const previousOrder = navigationData.ordersList[currentIndex - 1];
        if (previousOrder) {
            navigate(`/orders/${previousOrder._id}`, {
                state: { ...location.state, currentIndex: currentIndex - 1 }
            });
        }
    }, [navigationData, navigate, location.state]);

    const handleNextOrder = useCallback(() => {
        const currentIndex = navigationData.currentIndex;
        if (currentIndex >= navigationData.totalOrders - 1 || !navigationData.ordersList.length) return;
        const nextOrder = navigationData.ordersList[currentIndex + 1];
        if (nextOrder) {
            navigate(`/orders/${nextOrder._id}`, {
                state: { ...location.state, currentIndex: currentIndex + 1 }
            });
        }
    }, [navigationData, navigate, location.state]);

    const capitalizeFirstLetter = useCallback((string) => {
        return string?.charAt(0)?.toUpperCase() + string?.slice(1);
    }, []);

    const statusInfo = useMemo(() => {
        const statusColors = {
            accepted: 'primary',
            processing: 'info',
            'on-the-way': 'warning',
            delivered: 'success',
            rejected: 'danger'
        };
        const statusKey = orderDetails?.order_status?.toLowerCase();
        const color = statusColors[statusKey] || 'primary';
        return { color, statusKey };
    }, [orderDetails?.order_status]);

    const navigationInfo = useMemo(() => {
        const { currentIndex, totalOrders, ordersList } = navigationData;
        return {
            showNavigation: currentIndex !== -1 && totalOrders > 1,
            canGoPrevious: currentIndex > 0,
            canGoNext: currentIndex < totalOrders - 1,
            currentPosition: currentIndex + 1,
            totalCount: totalOrders
        };
    }, [navigationData]);

    // ============================================================
    // Compute discount/billing breakdown for display.
    // Branches on is_b2b_order because trade orders store a real
    // breakdown (order_value = ex-GST subtotal only; GST and
    // delivery tracked as separate fields), whereas consumer orders
    // fold delivery into order_value and bake GST invisibly into
    // tax-inclusive item pricing. Reusing the consumer formula for
    // trade orders produces a wrong subtotal and total — see the
    // b2b branch below for the fix.
    // ============================================================
    const billingBreakdown = useMemo(() => {
        if (!orderDetails) return null;

        const productDiscount = parseFloat(orderDetails.productDiscountAmount || 0);
        const couponDiscount = parseFloat(orderDetails.couponDiscountAmount || 0);
        const loyaltyDiscount = parseFloat(orderDetails.loyaltyDiscount || 0);
        const totalDiscount = parseFloat(orderDetails.discount_value || 0);
        const totalSavings = productDiscount + couponDiscount + loyaltyDiscount;

        // ── Trade (B2B) orders ──────────────────────────────────────────────
        if (orderDetails.is_b2b_order) {
            const subtotal = parseFloat(orderDetails.b2b_subtotal_ex_gst || 0);
            const gstAmount = parseFloat(orderDetails.b2b_gst_amount || 0);
            const deliveryCharges = parseFloat(
                orderDetails.b2b_delivery_charge ?? orderDetails.delivery_charges ?? 0
            );
            const finalTotal = parseFloat(orderDetails.b2b_total_inc_gst || 0);

            return {
                subtotal,
                gstAmount,
                isB2B: true,
                productDiscount,
                couponDiscount,
                loyaltyDiscount,
                totalDiscount,
                totalSavings,
                deliveryCharges,
                isFreeDelivery: deliveryCharges === 0,
                finalTotal,
                discountSource: orderDetails.discountSource || 'none',
                isStacked: orderDetails.discountSource === 'stacked',
                couponName: orderDetails.coupon?.coupan_name || orderDetails.coupon_name || null,
                loyaltyPointsUsed: orderDetails.loyaltyPointsUsed || 0,
            };
        }

        // ── Consumer orders — unchanged from original logic ────────────────
        const orderValue = parseFloat(orderDetails.order_value || 0);
        const deliveryCharges = parseFloat(orderDetails.delivery_charges || 0);

        // Subtotal (items only, before discounts) = order_value − delivery + total discounts
        const subtotal = orderValue - deliveryCharges + totalDiscount;
        const isFreeDelivery = deliveryCharges === 0 && (orderValue - deliveryCharges) >= 18;

        return {
            subtotal,
            gstAmount: 0,
            isB2B: false,
            productDiscount,
            couponDiscount,
            loyaltyDiscount,
            totalDiscount,
            totalSavings,
            deliveryCharges,
            isFreeDelivery,
            finalTotal: orderValue,
            discountSource: orderDetails.discountSource || 'none',
            isStacked: orderDetails.discountSource === 'stacked',
            couponName: orderDetails.coupon?.coupan_name || orderDetails.coupon_name || null,
            loyaltyPointsUsed: orderDetails.loyaltyPointsUsed || 0,
        };
    }, [orderDetails]);

    // Badge for discount source
    const discountSourceBadge = useMemo(() => {
        if (!billingBreakdown) return null;
        const sourceMap = {
            stacked: { label: 'Stacked', color: 'info', icon: <FaLayerGroup size={10} /> },
            coupon: { label: 'Coupon Only', color: 'success', icon: <FaTag size={10} /> },
            variant: { label: 'Product Offer', color: 'warning', icon: <FaPercentage size={10} /> },
            loyalty: { label: 'Loyalty', color: 'primary', icon: <FaGift size={10} /> },
            none: null
        };
        return sourceMap[billingBreakdown.discountSource] || null;
    }, [billingBreakdown]);

    useEffect(() => {
        getOrderDetails();
    }, [getOrderDetails]);

    const handleSaveAddress = async (addressId) => {
        const finalAddress = newAddress?.trim() || orderDetails.address?.address;
        setOrderDetails((prev) => ({
            ...prev,
            address: { ...prev.address, address: finalAddress },
        }));
        setNewAddress(finalAddress);
        setIsEditing(false);

        try {
            await axios.patch(
                `${baseURL}/api/customers/update-address/${addressId}`,
                { address: finalAddress },
                { headers: { Authorization: authData.token } }
            );
        } catch (err) {
            console.error("Error updating address:", err);
            getOrderDetails();
        }
    };

    if (loading) {
        return (
            <Container fluid>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mt-2">Loading order details...</p>
                    </div>
                </div>
            </Container>
        );
    }

    if (!orderDetails) {
        return (
            <Container fluid>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <h5>Order not found</h5>
                        <Button color="primary" onClick={handleBackToOrders}>Back to Orders</Button>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <>
            <Container fluid>
                <div className='page-title'>
                    <Row className='align-items-center'>
                        <Col xs='12'>
                            <div className='d-flex align-items-center justify-content-between'>
                                <div className='d-flex align-items-center'>
                                    <Button
                                        className='filter_btn me-3'
                                        onClick={handleBackToOrders}
                                        title={navigationData.isCustomerContext ? "Back to Customer Details" : "Back to Orders"}
                                    >
                                        <FaArrowLeft color='#d422ad' />
                                    </Button>
                                    <div className='d-flex align-items-center'>
                                        <p className='mb-0 d-flex align-items-center'>
                                            Order Id: <b className='ms-1'> {orderDetails.sequence_number}</b>
                                            <span style={{ color: '#E1E6EF' }} className='mx-2'> | </span>
                                            <span style={{ fontSize: '13px' }} className={`badge badge-light-${statusInfo.color}`}>
                                                {capitalizeFirstLetter(orderDetails.order_status)}
                                            </span>
                                        </p>
                                        {navigationData.isCustomerContext && navigationData.customerName && (
                                            <>
                                                <span style={{ color: '#E1E6EF' }} className='mx-2'> | </span>
                                                <div className='d-flex align-items-center text-muted'>
                                                    <FaUser size={12} className='me-1' />
                                                    <small>{navigationData.customerName}</small>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className='d-flex align-items-center'>
                                    {navigationInfo.showNavigation && (
                                        <div className='d-flex align-items-center me-3'>
                                            <Button
                                                className='filter_btn me-2'
                                                onClick={handlePreviousOrder}
                                                disabled={!navigationInfo.canGoPrevious}
                                                title="Previous Order"
                                            >
                                                <FaChevronLeft color={!navigationInfo.canGoPrevious ? '#ccc' : '#d422ad'} />
                                            </Button>
                                            <span className='mx-2 text-muted' style={{ fontSize: '14px' }}>
                                                {navigationInfo.currentPosition} of {navigationInfo.totalCount}
                                            </span>
                                            <Button
                                                className='filter_btn me-2'
                                                onClick={handleNextOrder}
                                                disabled={!navigationInfo.canGoNext}
                                                title="Next Order"
                                            >
                                                <FaChevronRight color={!navigationInfo.canGoNext ? '#ccc' : '#d422ad'} />
                                            </Button>
                                        </div>
                                    )}
                                    <Button
                                        className='filter_btn'
                                        onClick={() => downloadInvoice(id)}
                                        title="Download Invoice"
                                    >
                                        <FaDownload color='#d422ad' />
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Container>

            <Container fluid>
                <div className='order_details'>
                    <Row className="d-flex justify-content-center">
                        {/* Customer Details — unchanged */}
                        <Col lg={4} md={6} className="d-flex">
                            <Card className="flex-fill card-equal-height">
                                <CardHeader>
                                    <h5>Customer Details</h5>
                                </CardHeader>
                                <CardBody>
                                    <Table className='detail_table'>
                                        <tbody>
                                            <tr>
                                                <th>Customer Name:</th>
                                                <td>
                                                    {(orderDetails.address?.first_name && orderDetails.address?.last_name) ?
                                                        `${orderDetails.address.first_name} ${orderDetails.address.last_name}` : 'N/A'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Mobile Number:</th>
                                                <td>{orderDetails.address?.addressPhoneNumber || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <th>Email:</th>
                                                <td>{orderDetails.user?.[0]?.email || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <th>Address:</th>
                                                <td className="d-flex align-items-baseline justify-content-between">
                                                    {!isEditing ? (
                                                        <>
                                                            <span>{orderDetails.address?.address || "-"}</span>
                                                            <button
                                                                className="btn btn-sm btn-link text-primary p-0 ms-2"
                                                                onClick={() => {
                                                                    setNewAddress(orderDetails.address?.address || "");
                                                                    setIsEditing(true);
                                                                }}
                                                            >Edit</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                style={{
                                                                    width: "100%",
                                                                    fontSize: "0.705rem",
                                                                    lineHeight: "1.8",
                                                                    overflow: "hidden",
                                                                    resize: "none",
                                                                }}
                                                                rows={3}
                                                                value={newAddress}
                                                                onChange={(e) => {
                                                                    setNewAddress(e.target.value);
                                                                    e.target.style.height = "auto";
                                                                    e.target.style.height = e.target.scrollHeight + "px";
                                                                }}
                                                            />
                                                            <div className='flex flex-col gap-3'>
                                                                <button
                                                                    className="btn btn-sm btn-link text-success p-0 ms-2"
                                                                    onClick={() => handleSaveAddress(orderDetails.address?._id)}
                                                                >Save</button>
                                                                <button
                                                                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                                    onClick={() => setIsEditing(false)}
                                                                >Cancel</button>
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Order Details — unchanged */}
                        <Col lg={4} md={6} sm={12} className="d-flex">
                            <Card className="flex-fill card-equal-height">
                                <CardHeader>
                                    <h5>Order Details</h5>
                                </CardHeader>
                                <CardBody>
                                    <Table className='detail_table'>
                                        <tbody>
                                            <tr>
                                                <th>Order Created At:</th>
                                                <td>{moment(orderDetails.createdAt).format('DD MMM YYYY, h:mm:A')}</td>
                                            </tr>
                                            {orderDetails.order_status === 'cancelled' ? (
                                                <tr>
                                                    <th>Order Cancelled:</th>
                                                    <td>{orderDetails.cancel_reason || 'N/A'}</td>
                                                </tr>
                                            ) : (
                                                <tr>
                                                    <th>Order Delivered At:</th>
                                                    <td>
                                                        {orderDetails.order_status === 'delivered' ?
                                                            moment(orderDetails.updatedAt).format('DD MMM YYYY, h:mm:A') : 'Pending'}
                                                    </td>
                                                </tr>
                                            )}
                                            {orderDetails?.isScheduled && (
                                                <>
                                                    <tr>
                                                        <th>Scheduled Date:</th>
                                                        <td>
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#7c3aed', fontWeight: '600' }}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                                </svg>
                                                                {moment(orderDetails.scheduledDate).format('DD MMM YYYY')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th>Scheduled Time:</th>
                                                        <td>
                                                            {orderDetails.scheduledTimeSlot ? (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: '600' }}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                        <circle cx="12" cy="12" r="10" />
                                                                        <polyline points="12 6 12 12 16 14" />
                                                                    </svg>
                                                                    {orderDetails.scheduledTimeSlot.replace(
                                                                        /(\d{2}):(\d{2})-(\d{2}):(\d{2})/,
                                                                        (_, h1, m1, h2, m2) => {
                                                                            const fmt = (h, m) => {
                                                                                const hr = parseInt(h);
                                                                                return `${hr > 12 ? hr - 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
                                                                            };
                                                                            return `${fmt(h1, m1)} – ${fmt(h2, m2)}`;
                                                                        }
                                                                    )}
                                                                </span>
                                                            ) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Delivery Instructions — unchanged */}
                        <Col lg={4} md={6} sm={12} className="d-flex">
                            <Card className="flex-fill card-equal-height">
                                <CardHeader>
                                    <h5>Delivery Instructions</h5>
                                </CardHeader>
                                <CardBody>
                                    <p className="mb-0">{orderDetails.comments?.trim() || "No special instructions"}</p>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Product Details — unchanged */}
                        <Col lg={8} md={12} sm={12}>
                            <Card>
                                <CardHeader>
                                    <h5>Product Details</h5>
                                </CardHeader>
                                <CardBody>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>QUANTITY</th>
                                                <th>PRODUCT NAME</th>
                                                <th>PRICE</th>
                                                <th>TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderDetails.order_Variants?.length > 0 ? (
                                                orderDetails.order_Variants.map((data, index) => (
                                                    <tr key={index}>
                                                        <td>x {data.orderQuantity || 1}</td>
                                                        <td>
                                                            <div className="d-flex border-0 align-items-center">
                                                                <img
                                                                    width="30"
                                                                    height="30"
                                                                    src={data.variant?.variantImage ? `${imageURL}${data.variant.variantImage}` : dummyImg}
                                                                    alt={data.variant?.variantName || "Product Image"}
                                                                    loading="lazy"
                                                                />
                                                                <span className="ms-2">{data.variant?.variantName || 'N/A'}</span>
                                                            </div>
                                                        </td>
                                                        <td>$ {data.variant?.finalSellingPrice?.toFixed(2) || '0.00'}</td>
                                                        <td>$ {((data.orderQuantity || 1) * (data.variant?.finalSellingPrice || 0)).toFixed(2)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No Products Found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ============================================== */}
                        {/* PRODUCT BILLING — fully redesigned             */}
                        {/* ============================================== */}
                        <Col lg={4} md={6} sm={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Billing Summary</h5>
                                    {discountSourceBadge && (
                                        <Badge
                                            color={discountSourceBadge.color}
                                            pill
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '11px',
                                                padding: '5px 10px'
                                            }}
                                        >
                                            {discountSourceBadge.icon}
                                            {discountSourceBadge.label}
                                        </Badge>
                                    )}
                                </CardHeader>
                                <CardBody>
                                    <div className="billing-breakdown">
                                        {/* Subtotal */}
                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="text-muted">Subtotal {billingBreakdown.isB2B ? '(ex GST)' : ''}</span>
                                            <span className="fw-semibold">
                                                ${billingBreakdown.subtotal.toFixed(2)}
                                            </span>
                                        </div>

                                        {/* GST line — trade orders only; consumer prices are tax-inclusive */}
                                        {billingBreakdown.isB2B && (
                                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                                <span className="text-muted">GST (10%)</span>
                                                <span className="fw-semibold">
                                                    ${billingBreakdown.gstAmount.toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Product Discount line */}
                                        {billingBreakdown.productDiscount > 0 && (
                                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                                <span className="d-flex align-items-center text-muted">
                                                    <FaPercentage size={11} className="me-2 text-warning" />
                                                    Product Discount
                                                </span>
                                                <span className="text-danger fw-semibold">
                                                    −${billingBreakdown.productDiscount.toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Coupon Discount line */}
                                        {billingBreakdown.couponDiscount > 0 && (
                                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                                <span className="d-flex align-items-center text-muted">
                                                    <FaTag size={11} className="me-2 text-success" />
                                                    Coupon Discount
                                                    {billingBreakdown.couponName && (
                                                        <Badge
                                                            color="success"
                                                            className="ms-2"
                                                            style={{ fontSize: '10px', fontWeight: 600 }}
                                                            pill
                                                        >
                                                            {billingBreakdown.couponName}
                                                        </Badge>
                                                    )}
                                                </span>
                                                <span className="text-danger fw-semibold">
                                                    −${billingBreakdown.couponDiscount.toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Loyalty Discount line */}
                                        {billingBreakdown.loyaltyDiscount > 0 && (
                                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                                <span className="d-flex align-items-center text-muted">
                                                    <FaGift size={11} className="me-2 text-primary" />
                                                    Loyalty Points
                                                    {billingBreakdown.loyaltyPointsUsed > 0 && (
                                                        <small className="text-muted ms-2">
                                                            ({billingBreakdown.loyaltyPointsUsed} pts)
                                                        </small>
                                                    )}
                                                </span>
                                                <span className="text-danger fw-semibold">
                                                    −${billingBreakdown.loyaltyDiscount.toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Delivery */}
                                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                            <span className="text-muted">Delivery</span>
                                            <span>
                                                {billingBreakdown.deliveryCharges === 0 ? (
                                                    <>
                                                        {!billingBreakdown.isB2B && (
                                                            <span className="text-decoration-line-through text-muted me-2" style={{ fontSize: '12px' }}>
                                                                $9.99
                                                            </span>
                                                        )}
                                                        <span className="text-success fw-semibold">FREE</span>
                                                    </>
                                                ) : (
                                                    <span className="fw-semibold">
                                                        ${billingBreakdown.deliveryCharges.toFixed(2)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>

                                        {/* Total */}
                                        <div
                                            className="d-flex justify-content-between align-items-center py-3 mt-2"
                                            style={{
                                                borderTop: '2px dashed #d3d3d3',
                                                borderBottom: '2px dashed #d3d3d3'
                                            }}
                                        >
                                            <span className="fw-bold" style={{ fontSize: '16px' }}>Total Paid {billingBreakdown.isB2B ? '(inc GST)' : ''}</span>
                                            <span className="fw-bold" style={{ fontSize: '20px', color: '#d422ad' }}>
                                                ${billingBreakdown.finalTotal.toFixed(2)}
                                            </span>
                                        </div>

                                        {/* Total Savings banner */}
                                        {billingBreakdown.totalSavings > 0 && (
                                            <div
                                                className="text-center mt-3 py-2 px-3 rounded"
                                                style={{
                                                    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                                    border: '1px solid #6ee7b7'
                                                }}
                                            >
                                                <div className="text-success fw-bold" style={{ fontSize: '14px' }}>
                                                    🎉 Customer saved ${billingBreakdown.totalSavings.toFixed(2)} on this order
                                                </div>
                                            </div>
                                        )}

                                        {/* Stacked indicator note */}
                                        {billingBreakdown.isStacked && (
                                            <div
                                                className="mt-2 px-2 py-1 rounded text-center"
                                                style={{
                                                    background: '#fef3c7',
                                                    border: '1px solid #fde68a',
                                                    fontSize: '11px',
                                                    color: '#92400e'
                                                }}
                                            >
                                                ⚡ Coupon stacked with product discount (override enabled)
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Container>
        </>
    )
}

export default ViewOrder

//Before adding b2b price and discount
// import React, { useEffect, useState, useMemo, useCallback } from 'react'
// import { Button, Card, CardBody, CardHeader, Col, Container, Row, Table, Badge } from 'reactstrap'
// import { FaDownload, FaArrowLeft, FaChevronLeft, FaChevronRight, FaUser, FaTag, FaGift, FaPercentage, FaLayerGroup } from 'react-icons/fa'
// import dummyImg from '../../../../../../src/assets/images/product/1.png'
// import { useParams, useNavigate, useLocation } from 'react-router'
// import axios from 'axios'
// import { baseURL, imageURL, orderURL } from '../../../../../Services/api/baseURL'
// import moment from 'moment';

// function ViewOrder() {
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const location = useLocation();
//     const [orderDetails, setOrderDetails] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [navigationData, setNavigationData] = useState({
//         currentIndex: -1,
//         totalOrders: 0,
//         ordersList: [],
//         filters: {},
//         isCustomerContext: false,
//         customerName: ''
//     });
//     const [isEditing, setIsEditing] = useState(false);
//     const [newAddress, setNewAddress] = useState(null);

//     const authData = useMemo(() => {
//         try {
//             return {
//                 token: JSON.parse(localStorage.getItem("token")),
//                 userRole: JSON.parse(localStorage.getItem("role_name")),
//                 userData: JSON.parse(localStorage.getItem("UserData"))
//             };
//         } catch (error) {
//             console.error('Error parsing auth data:', error);
//             return { token: null, userRole: null, userData: null };
//         }
//     }, []);

//     useEffect(() => {
//         if (location.state) {
//             setNavigationData({
//                 currentIndex: location.state.currentIndex !== undefined ? location.state.currentIndex : -1,
//                 totalOrders: location.state.totalOrders || 0,
//                 ordersList: location.state.ordersList || [],
//                 filters: location.state.filters || {},
//                 isCustomerContext: location.state.isCustomerContext || false,
//                 customerName: location.state.customerName || ''
//             });
//         }
//     }, [location.state]);

//     const getOrderDetails = useCallback(async () => {
//         if (!authData.token || !id) return;
//         setLoading(true);
//         try {
//             const response = await axios.get(`${orderURL}/get-order/${id}`, {
//                 headers: { Authorization: `${authData.token}` }
//             });
//             if (response?.status === 200) {
//                 setOrderDetails(response.data.data);
//             }
//         } catch (err) {
//             console.error('Error fetching order details:', err);
//         } finally {
//             setLoading(false);
//         }
//     }, [id, authData.token]);

//     const downloadInvoice = useCallback(async (orderId) => {
//         if (!authData.token) return;
//         try {
//             const response = await axios.get(`${orderURL}/invoice-pdf/${orderId}`, {
//                 responseType: 'blob',
//                 headers: { Authorization: `${authData.token}` }
//             });
//             const blob = new Blob([response.data], { type: 'application/pdf' });
//             const link = document.createElement('a');
//             link.href = window.URL.createObjectURL(blob);
//             link.download = `invoice-${orderId}.pdf`;
//             link.click();
//             window.URL.revokeObjectURL(link.href);
//         } catch (error) {
//             console.error('Error downloading the invoice:', error);
//         }
//     }, [authData.token]);

//     const handleBackToOrders = useCallback(() => {
//         if (navigationData.isCustomerContext && navigationData.filters?.customerId) {
//             navigate(`/customers/customer-detail/${navigationData.filters.customerId}`);
//         } else {
//             navigate('/orders');
//         }
//     }, [navigate, navigationData]);

//     const handlePreviousOrder = useCallback(() => {
//         const currentIndex = navigationData.currentIndex;
//         if (currentIndex <= 0 || !navigationData.ordersList.length) return;
//         const previousOrder = navigationData.ordersList[currentIndex - 1];
//         if (previousOrder) {
//             navigate(`/orders/${previousOrder._id}`, {
//                 state: { ...location.state, currentIndex: currentIndex - 1 }
//             });
//         }
//     }, [navigationData, navigate, location.state]);

//     const handleNextOrder = useCallback(() => {
//         const currentIndex = navigationData.currentIndex;
//         if (currentIndex >= navigationData.totalOrders - 1 || !navigationData.ordersList.length) return;
//         const nextOrder = navigationData.ordersList[currentIndex + 1];
//         if (nextOrder) {
//             navigate(`/orders/${nextOrder._id}`, {
//                 state: { ...location.state, currentIndex: currentIndex + 1 }
//             });
//         }
//     }, [navigationData, navigate, location.state]);

//     const capitalizeFirstLetter = useCallback((string) => {
//         return string?.charAt(0)?.toUpperCase() + string?.slice(1);
//     }, []);

//     const statusInfo = useMemo(() => {
//         const statusColors = {
//             accepted: 'primary',
//             processing: 'info',
//             'on-the-way': 'warning',
//             delivered: 'success',
//             rejected: 'danger'
//         };
//         const statusKey = orderDetails?.order_status?.toLowerCase();
//         const color = statusColors[statusKey] || 'primary';
//         return { color, statusKey };
//     }, [orderDetails?.order_status]);

//     const navigationInfo = useMemo(() => {
//         const { currentIndex, totalOrders, ordersList } = navigationData;
//         return {
//             showNavigation: currentIndex !== -1 && totalOrders > 1,
//             canGoPrevious: currentIndex > 0,
//             canGoNext: currentIndex < totalOrders - 1,
//             currentPosition: currentIndex + 1,
//             totalCount: totalOrders
//         };
//     }, [navigationData]);

//     // ============================================================
//     // NEW: Compute discount breakdown for display
//     // ============================================================
//     const billingBreakdown = useMemo(() => {
//         if (!orderDetails) return null;

//         // Calculate gross subtotal (before any discount, before delivery)
//         const orderValue = parseFloat(orderDetails.order_value || 0);
//         const deliveryCharges = parseFloat(orderDetails.delivery_charges || 0);
//         const productDiscount = parseFloat(orderDetails.productDiscountAmount || 0);
//         const couponDiscount = parseFloat(orderDetails.couponDiscountAmount || 0);
//         const loyaltyDiscount = parseFloat(orderDetails.loyaltyDiscount || 0);
//         const totalDiscount = parseFloat(orderDetails.discount_value || 0);

//         // Subtotal (items only, before discounts) = order_value − delivery + total discounts
//         const subtotal = orderValue - deliveryCharges + totalDiscount;

//         const totalSavings = productDiscount + couponDiscount + loyaltyDiscount;
//         const isFreeDelivery = deliveryCharges === 0 && (orderValue - deliveryCharges) >= 18;

//         return {
//             subtotal,
//             productDiscount,
//             couponDiscount,
//             loyaltyDiscount,
//             totalDiscount,
//             totalSavings,
//             deliveryCharges,
//             isFreeDelivery,
//             finalTotal: orderValue,
//             discountSource: orderDetails.discountSource || 'none',
//             isStacked: orderDetails.discountSource === 'stacked',
//             couponName: orderDetails.coupon?.coupan_name || orderDetails.coupon_name || null,
//             loyaltyPointsUsed: orderDetails.loyaltyPointsUsed || 0,
//         };
//     }, [orderDetails]);

//     // Badge for discount source
//     const discountSourceBadge = useMemo(() => {
//         if (!billingBreakdown) return null;
//         const sourceMap = {
//             stacked: { label: 'Stacked', color: 'info', icon: <FaLayerGroup size={10} /> },
//             coupon: { label: 'Coupon Only', color: 'success', icon: <FaTag size={10} /> },
//             variant: { label: 'Product Offer', color: 'warning', icon: <FaPercentage size={10} /> },
//             loyalty: { label: 'Loyalty', color: 'primary', icon: <FaGift size={10} /> },
//             none: null
//         };
//         return sourceMap[billingBreakdown.discountSource] || null;
//     }, [billingBreakdown]);

//     useEffect(() => {
//         getOrderDetails();
//     }, [getOrderDetails]);

//     const handleSaveAddress = async (addressId) => {
//         const finalAddress = newAddress?.trim() || orderDetails.address?.address;
//         setOrderDetails((prev) => ({
//             ...prev,
//             address: { ...prev.address, address: finalAddress },
//         }));
//         setNewAddress(finalAddress);
//         setIsEditing(false);

//         try {
//             await axios.patch(
//                 `${baseURL}/api/customers/update-address/${addressId}`,
//                 { address: finalAddress },
//                 { headers: { Authorization: authData.token } }
//             );
//         } catch (err) {
//             console.error("Error updating address:", err);
//             getOrderDetails();
//         }
//     };

//     if (loading) {
//         return (
//             <Container fluid>
//                 <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
//                     <div className="text-center">
//                         <div className="spinner-border text-primary" role="status">
//                             <span className="sr-only">Loading...</span>
//                         </div>
//                         <p className="mt-2">Loading order details...</p>
//                     </div>
//                 </div>
//             </Container>
//         );
//     }

//     if (!orderDetails) {
//         return (
//             <Container fluid>
//                 <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
//                     <div className="text-center">
//                         <h5>Order not found</h5>
//                         <Button color="primary" onClick={handleBackToOrders}>Back to Orders</Button>
//                     </div>
//                 </div>
//             </Container>
//         );
//     }

//     return (
//         <>
//             <Container fluid>
//                 <div className='page-title'>
//                     <Row className='align-items-center'>
//                         <Col xs='12'>
//                             <div className='d-flex align-items-center justify-content-between'>
//                                 <div className='d-flex align-items-center'>
//                                     <Button
//                                         className='filter_btn me-3'
//                                         onClick={handleBackToOrders}
//                                         title={navigationData.isCustomerContext ? "Back to Customer Details" : "Back to Orders"}
//                                     >
//                                         <FaArrowLeft color='#d422ad' />
//                                     </Button>
//                                     <div className='d-flex align-items-center'>
//                                         <p className='mb-0 d-flex align-items-center'>
//                                             Order Id: <b className='ms-1'> {orderDetails.sequence_number}</b>
//                                             <span style={{ color: '#E1E6EF' }} className='mx-2'> | </span>
//                                             <span style={{ fontSize: '13px' }} className={`badge badge-light-${statusInfo.color}`}>
//                                                 {capitalizeFirstLetter(orderDetails.order_status)}
//                                             </span>
//                                         </p>
//                                         {navigationData.isCustomerContext && navigationData.customerName && (
//                                             <>
//                                                 <span style={{ color: '#E1E6EF' }} className='mx-2'> | </span>
//                                                 <div className='d-flex align-items-center text-muted'>
//                                                     <FaUser size={12} className='me-1' />
//                                                     <small>{navigationData.customerName}</small>
//                                                 </div>
//                                             </>
//                                         )}
//                                     </div>
//                                 </div>
//                                 <div className='d-flex align-items-center'>
//                                     {navigationInfo.showNavigation && (
//                                         <div className='d-flex align-items-center me-3'>
//                                             <Button
//                                                 className='filter_btn me-2'
//                                                 onClick={handlePreviousOrder}
//                                                 disabled={!navigationInfo.canGoPrevious}
//                                                 title="Previous Order"
//                                             >
//                                                 <FaChevronLeft color={!navigationInfo.canGoPrevious ? '#ccc' : '#d422ad'} />
//                                             </Button>
//                                             <span className='mx-2 text-muted' style={{ fontSize: '14px' }}>
//                                                 {navigationInfo.currentPosition} of {navigationInfo.totalCount}
//                                             </span>
//                                             <Button
//                                                 className='filter_btn me-2'
//                                                 onClick={handleNextOrder}
//                                                 disabled={!navigationInfo.canGoNext}
//                                                 title="Next Order"
//                                             >
//                                                 <FaChevronRight color={!navigationInfo.canGoNext ? '#ccc' : '#d422ad'} />
//                                             </Button>
//                                         </div>
//                                     )}
//                                     <Button
//                                         className='filter_btn'
//                                         onClick={() => downloadInvoice(id)}
//                                         title="Download Invoice"
//                                     >
//                                         <FaDownload color='#d422ad' />
//                                     </Button>
//                                 </div>
//                             </div>
//                         </Col>
//                     </Row>
//                 </div>
//             </Container>

//             <Container fluid>
//                 <div className='order_details'>
//                     <Row className="d-flex justify-content-center">
//                         {/* Customer Details — unchanged */}
//                         <Col lg={4} md={6} className="d-flex">
//                             <Card className="flex-fill card-equal-height">
//                                 <CardHeader>
//                                     <h5>Customer Details</h5>
//                                 </CardHeader>
//                                 <CardBody>
//                                     <Table className='detail_table'>
//                                         <tbody>
//                                             <tr>
//                                                 <th>Customer Name:</th>
//                                                 <td>
//                                                     {(orderDetails.address?.first_name && orderDetails.address?.last_name) ?
//                                                         `${orderDetails.address.first_name} ${orderDetails.address.last_name}` : 'N/A'}
//                                                 </td>
//                                             </tr>
//                                             <tr>
//                                                 <th>Mobile Number:</th>
//                                                 <td>{orderDetails.address?.addressPhoneNumber || 'N/A'}</td>
//                                             </tr>
//                                             <tr>
//                                                 <th>Email:</th>
//                                                 <td>{orderDetails.user?.[0]?.email || 'N/A'}</td>
//                                             </tr>
//                                             <tr>
//                                                 <th>Address:</th>
//                                                 <td className="d-flex align-items-baseline justify-content-between">
//                                                     {!isEditing ? (
//                                                         <>
//                                                             <span>{orderDetails.address?.address || "-"}</span>
//                                                             <button
//                                                                 className="btn btn-sm btn-link text-primary p-0 ms-2"
//                                                                 onClick={() => {
//                                                                     setNewAddress(orderDetails.address?.address || "");
//                                                                     setIsEditing(true);
//                                                                 }}
//                                                             >Edit</button>
//                                                         </>
//                                                     ) : (
//                                                         <>
//                                                             <textarea
//                                                                 className="form-control form-control-sm"
//                                                                 style={{
//                                                                     width: "100%",
//                                                                     fontSize: "0.705rem",
//                                                                     lineHeight: "1.8",
//                                                                     overflow: "hidden",
//                                                                     resize: "none",
//                                                                 }}
//                                                                 rows={3}
//                                                                 value={newAddress}
//                                                                 onChange={(e) => {
//                                                                     setNewAddress(e.target.value);
//                                                                     e.target.style.height = "auto";
//                                                                     e.target.style.height = e.target.scrollHeight + "px";
//                                                                 }}
//                                                             />
//                                                             <div className='flex flex-col gap-3'>
//                                                                 <button
//                                                                     className="btn btn-sm btn-link text-success p-0 ms-2"
//                                                                     onClick={() => handleSaveAddress(orderDetails.address?._id)}
//                                                                 >Save</button>
//                                                                 <button
//                                                                     className="btn btn-sm btn-link text-danger p-0 ms-2"
//                                                                     onClick={() => setIsEditing(false)}
//                                                                 >Cancel</button>
//                                                             </div>
//                                                         </>
//                                                     )}
//                                                 </td>
//                                             </tr>
//                                         </tbody>
//                                     </Table>
//                                 </CardBody>
//                             </Card>
//                         </Col>

//                         {/* Order Details — unchanged */}
//                         <Col lg={4} md={6} sm={12} className="d-flex">
//                             <Card className="flex-fill card-equal-height">
//                                 <CardHeader>
//                                     <h5>Order Details</h5>
//                                 </CardHeader>
//                                 <CardBody>
//                                     <Table className='detail_table'>
//                                         <tbody>
//                                             <tr>
//                                                 <th>Order Created At:</th>
//                                                 <td>{moment(orderDetails.createdAt).format('DD MMM YYYY, h:mm:A')}</td>
//                                             </tr>
//                                             {orderDetails.order_status === 'cancelled' ? (
//                                                 <tr>
//                                                     <th>Order Cancelled:</th>
//                                                     <td>{orderDetails.cancel_reason || 'N/A'}</td>
//                                                 </tr>
//                                             ) : (
//                                                 <tr>
//                                                     <th>Order Delivered At:</th>
//                                                     <td>
//                                                         {orderDetails.order_status === 'delivered' ?
//                                                             moment(orderDetails.updatedAt).format('DD MMM YYYY, h:mm:A') : 'Pending'}
//                                                     </td>
//                                                 </tr>
//                                             )}
//                                             {orderDetails?.isScheduled && (
//                                                 <>
//                                                     <tr>
//                                                         <th>Scheduled Date:</th>
//                                                         <td>
//                                                             <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#7c3aed', fontWeight: '600' }}>
//                                                                 <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//                                                                     <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
//                                                                     <line x1="16" y1="2" x2="16" y2="6" />
//                                                                     <line x1="8" y1="2" x2="8" y2="6" />
//                                                                     <line x1="3" y1="10" x2="21" y2="10" />
//                                                                 </svg>
//                                                                 {moment(orderDetails.scheduledDate).format('DD MMM YYYY')}
//                                                             </span>
//                                                         </td>
//                                                     </tr>
//                                                     <tr>
//                                                         <th>Scheduled Time:</th>
//                                                         <td>
//                                                             {orderDetails.scheduledTimeSlot ? (
//                                                                 <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: '600' }}>
//                                                                     <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//                                                                         <circle cx="12" cy="12" r="10" />
//                                                                         <polyline points="12 6 12 12 16 14" />
//                                                                     </svg>
//                                                                     {orderDetails.scheduledTimeSlot.replace(
//                                                                         /(\d{2}):(\d{2})-(\d{2}):(\d{2})/,
//                                                                         (_, h1, m1, h2, m2) => {
//                                                                             const fmt = (h, m) => {
//                                                                                 const hr = parseInt(h);
//                                                                                 return `${hr > 12 ? hr - 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
//                                                                             };
//                                                                             return `${fmt(h1, m1)} – ${fmt(h2, m2)}`;
//                                                                         }
//                                                                     )}
//                                                                 </span>
//                                                             ) : 'N/A'}
//                                                         </td>
//                                                     </tr>
//                                                 </>
//                                             )}
//                                         </tbody>
//                                     </Table>
//                                 </CardBody>
//                             </Card>
//                         </Col>

//                         {/* Delivery Instructions — unchanged */}
//                         <Col lg={4} md={6} sm={12} className="d-flex">
//                             <Card className="flex-fill card-equal-height">
//                                 <CardHeader>
//                                     <h5>Delivery Instructions</h5>
//                                 </CardHeader>
//                                 <CardBody>
//                                     <p className="mb-0">{orderDetails.comments?.trim() || "No special instructions"}</p>
//                                 </CardBody>
//                             </Card>
//                         </Col>

//                         {/* Product Details — unchanged */}
//                         <Col lg={8} md={12} sm={12}>
//                             <Card>
//                                 <CardHeader>
//                                     <h5>Product Details</h5>
//                                 </CardHeader>
//                                 <CardBody>
//                                     <Table>
//                                         <thead>
//                                             <tr>
//                                                 <th>QUANTITY</th>
//                                                 <th>PRODUCT NAME</th>
//                                                 <th>PRICE</th>
//                                                 <th>TOTAL</th>
//                                             </tr>
//                                         </thead>
//                                         <tbody>
//                                             {orderDetails.order_Variants?.length > 0 ? (
//                                                 orderDetails.order_Variants.map((data, index) => (
//                                                     <tr key={index}>
//                                                         <td>x {data.orderQuantity || 1}</td>
//                                                         <td>
//                                                             <div className="d-flex border-0 align-items-center">
//                                                                 <img
//                                                                     width="30"
//                                                                     height="30"
//                                                                     src={data.variant?.variantImage ? `${imageURL}${data.variant.variantImage}` : dummyImg}
//                                                                     alt={data.variant?.variantName || "Product Image"}
//                                                                     loading="lazy"
//                                                                 />
//                                                                 <span className="ms-2">{data.variant?.variantName || 'N/A'}</span>
//                                                             </div>
//                                                         </td>
//                                                         <td>$ {data.variant?.finalSellingPrice?.toFixed(2) || '0.00'}</td>
//                                                         <td>$ {((data.orderQuantity || 1) * (data.variant?.finalSellingPrice || 0)).toFixed(2)}</td>
//                                                     </tr>
//                                                 ))
//                                             ) : (
//                                                 <tr>
//                                                     <td colSpan="4" className="text-center">No Products Found</td>
//                                                 </tr>
//                                             )}
//                                         </tbody>
//                                     </Table>
//                                 </CardBody>
//                             </Card>
//                         </Col>

//                         {/* ============================================== */}
//                         {/* PRODUCT BILLING — fully redesigned             */}
//                         {/* ============================================== */}
//                         <Col lg={4} md={6} sm={12}>
//                             <Card>
//                                 <CardHeader className="d-flex justify-content-between align-items-center">
//                                     <h5 className="mb-0">Billing Summary</h5>
//                                     {discountSourceBadge && (
//                                         <Badge
//                                             color={discountSourceBadge.color}
//                                             pill
//                                             style={{
//                                                 display: 'inline-flex',
//                                                 alignItems: 'center',
//                                                 gap: '4px',
//                                                 fontSize: '11px',
//                                                 padding: '5px 10px'
//                                             }}
//                                         >
//                                             {discountSourceBadge.icon}
//                                             {discountSourceBadge.label}
//                                         </Badge>
//                                     )}
//                                 </CardHeader>
//                                 <CardBody>
//                                     <div className="billing-breakdown">
//                                         {/* Subtotal */}
//                                         <div className="d-flex justify-content-between align-items-center py-2">
//                                             <span className="text-muted">Subtotal</span>
//                                             <span className="fw-semibold">
//                                                 ${billingBreakdown.subtotal.toFixed(2)}
//                                             </span>
//                                         </div>

//                                         {/* Product Discount line */}
//                                         {billingBreakdown.productDiscount > 0 && (
//                                             <div className="d-flex justify-content-between align-items-center py-2 border-top">
//                                                 <span className="d-flex align-items-center text-muted">
//                                                     <FaPercentage size={11} className="me-2 text-warning" />
//                                                     Product Discount
//                                                 </span>
//                                                 <span className="text-danger fw-semibold">
//                                                     −${billingBreakdown.productDiscount.toFixed(2)}
//                                                 </span>
//                                             </div>
//                                         )}

//                                         {/* Coupon Discount line */}
//                                         {billingBreakdown.couponDiscount > 0 && (
//                                             <div className="d-flex justify-content-between align-items-center py-2 border-top">
//                                                 <span className="d-flex align-items-center text-muted">
//                                                     <FaTag size={11} className="me-2 text-success" />
//                                                     Coupon Discount
//                                                     {billingBreakdown.couponName && (
//                                                         <Badge
//                                                             color="success"
//                                                             className="ms-2"
//                                                             style={{ fontSize: '10px', fontWeight: 600 }}
//                                                             pill
//                                                         >
//                                                             {billingBreakdown.couponName}
//                                                         </Badge>
//                                                     )}
//                                                 </span>
//                                                 <span className="text-danger fw-semibold">
//                                                     −${billingBreakdown.couponDiscount.toFixed(2)}
//                                                 </span>
//                                             </div>
//                                         )}

//                                         {/* Loyalty Discount line */}
//                                         {billingBreakdown.loyaltyDiscount > 0 && (
//                                             <div className="d-flex justify-content-between align-items-center py-2 border-top">
//                                                 <span className="d-flex align-items-center text-muted">
//                                                     <FaGift size={11} className="me-2 text-primary" />
//                                                     Loyalty Points
//                                                     {billingBreakdown.loyaltyPointsUsed > 0 && (
//                                                         <small className="text-muted ms-2">
//                                                             ({billingBreakdown.loyaltyPointsUsed} pts)
//                                                         </small>
//                                                     )}
//                                                 </span>
//                                                 <span className="text-danger fw-semibold">
//                                                     −${billingBreakdown.loyaltyDiscount.toFixed(2)}
//                                                 </span>
//                                             </div>
//                                         )}

//                                         {/* Delivery */}
//                                         <div className="d-flex justify-content-between align-items-center py-2 border-top">
//                                             <span className="text-muted">Delivery</span>
//                                             <span>
//                                                 {billingBreakdown.deliveryCharges === 0 ? (
//                                                     <>
//                                                         <span className="text-decoration-line-through text-muted me-2" style={{ fontSize: '12px' }}>
//                                                             $9.99
//                                                         </span>
//                                                         <span className="text-success fw-semibold">FREE</span>
//                                                     </>
//                                                 ) : (
//                                                     <span className="fw-semibold">
//                                                         ${billingBreakdown.deliveryCharges.toFixed(2)}
//                                                     </span>
//                                                 )}
//                                             </span>
//                                         </div>

//                                         {/* Total */}
//                                         <div
//                                             className="d-flex justify-content-between align-items-center py-3 mt-2"
//                                             style={{
//                                                 borderTop: '2px dashed #d3d3d3',
//                                                 borderBottom: '2px dashed #d3d3d3'
//                                             }}
//                                         >
//                                             <span className="fw-bold" style={{ fontSize: '16px' }}>Total Paid</span>
//                                             <span className="fw-bold" style={{ fontSize: '20px', color: '#d422ad' }}>
//                                                 ${billingBreakdown.finalTotal.toFixed(2)}
//                                             </span>
//                                         </div>

//                                         {/* Total Savings banner */}
//                                         {billingBreakdown.totalSavings > 0 && (
//                                             <div
//                                                 className="text-center mt-3 py-2 px-3 rounded"
//                                                 style={{
//                                                     background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
//                                                     border: '1px solid #6ee7b7'
//                                                 }}
//                                             >
//                                                 <div className="text-success fw-bold" style={{ fontSize: '14px' }}>
//                                                     🎉 Customer saved ${billingBreakdown.totalSavings.toFixed(2)} on this order
//                                                 </div>
//                                             </div>
//                                         )}

//                                         {/* Stacked indicator note */}
//                                         {billingBreakdown.isStacked && (
//                                             <div
//                                                 className="mt-2 px-2 py-1 rounded text-center"
//                                                 style={{
//                                                     background: '#fef3c7',
//                                                     border: '1px solid #fde68a',
//                                                     fontSize: '11px',
//                                                     color: '#92400e'
//                                                 }}
//                                             >
//                                                 ⚡ Coupon stacked with product discount (override enabled)
//                                             </div>
//                                         )}
//                                     </div>
//                                 </CardBody>
//                             </Card>
//                         </Col>
//                     </Row>
//                 </div>
//             </Container>
//         </>
//     )
// }

// export default ViewOrder

